/**
 * 세션 keepalive (클라이언트 전용)
 * ------------------------------------------------------------------
 * 사용자가 화면에서 실제로 작업 중이면, 액세스 토큰이 만료되기 직전에 조용히
 * 갱신(refresh)해 세션 만료 모달이 뜨는 것을 예방한다.
 *
 * 안전장치:
 *  - 로그인 상태(accessToken·refreshToken 존재)일 때만 동작
 *  - 최근 활동이 있을 때만 갱신 → 자리를 비우면(무활동) 만료를 허용(보안)
 *  - 만료 임박(REFRESH_BEFORE) 시에만 1회 갱신, 실패 시 기존 401 복구 흐름에 위임
 * 기존 로직을 바꾸지 않고 refresh 호출만 선제적으로 추가하므로 부작용이 없다.
 */
import { useAuthStore } from '~/stores/auth'

const IDLE_LIMIT     = 30 * 60 * 1000  // 30분 이상 무활동이면 갱신하지 않음
const REFRESH_BEFORE = 2  * 60 * 1000  // 만료 2분 전부터 갱신 시도
const CHECK_INTERVAL = 60 * 1000       // 1분마다 점검

/** JWT payload 의 exp(초) → 만료 시각(ms). 파싱 실패 시 null. */
function tokenExpMs(token: string): number | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(base64)) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export default defineNuxtPlugin(() => {
  const authStore = useAuthStore()

  let lastActivity = Date.now()
  const markActivity = () => { lastActivity = Date.now() }
  ;['mousedown', 'keydown', 'touchstart', 'scroll'].forEach((ev) =>
    window.addEventListener(ev, markActivity, { passive: true }),
  )

  let refreshing = false
  const timer = window.setInterval(async () => {
    if (!authStore.accessToken || !authStore.refreshToken) return   // 비로그인
    if (Date.now() - lastActivity > IDLE_LIMIT) return              // 자리 비움 → 만료 허용
    const exp = tokenExpMs(authStore.accessToken)
    if (exp == null) return
    if (exp - Date.now() > REFRESH_BEFORE) return                   // 아직 여유 있음
    if (refreshing) return

    refreshing = true
    try {
      await authStore.refresh()
    } catch {
      // 갱신 실패(리프레시 토큰까지 만료 등)는 기존 api.ts 401 흐름이 처리한다.
    } finally {
      refreshing = false
    }
  }, CHECK_INTERVAL)

  // HMR/언마운트 시 타이머 정리
  if (import.meta.hot) {
    import.meta.hot.dispose(() => window.clearInterval(timer))
  }
})
